import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useNotify, useGetIdentity, useGetOne, useGetManyReference, useGetMany, useGetList } from 'react-admin';
import { getToken } from '../../authentication/authProvider';

const PAPERMAN_API_URL = process.env.REACT_APP_PAPERMAN_API_URL;

const PROVINCE_CODES: Record<string, string> = {
  quebec: 'QC', québec: 'QC', ontario: 'ON', alberta: 'AB',
  'british columbia': 'BC', 'colombie-britannique': 'BC', manitoba: 'MB',
  'new brunswick': 'NB', 'nouveau-brunswick': 'NB',
  'newfoundland and labrador': 'NL', 'nova scotia': 'NS', 'nouvelle-écosse': 'NS',
  'prince edward island': 'PE', saskatchewan: 'SK',
  'northwest territories': 'NT', nunavut: 'NU', yukon: 'YT',
};

const provinceCode = (p?: string | null): string => {
  if (!p) return '';
  const s = String(p).trim();
  if (s.length === 2) return s.toUpperCase();
  return PROVINCE_CODES[s.toLowerCase()] || s;
};

// "City, Province" (e.g. "Montreal, QC") from a place_of_death_address jsonb.
const cityProvinceFromAddress = (addr: any): string => {
  if (!addr || typeof addr !== 'object') return '';
  const city = addr.municipalite || addr.ville || addr.city || '';
  const prov = provinceCode(addr.province);
  return [city, prov].filter(Boolean).join(', ');
};

// Select value for "don't print a marital status" — distinct from '' (not chosen yet).
const LEAVE_OFF = '__leave_off__';

// "Divorcé/Divorced" → "Divorced": staff read the dialog in English.
const englishHalf = (name?: string | null): string => {
  const halves = String(name ?? '').split(/\s*\/\s*/).filter(Boolean);
  return halves.length > 1 ? halves[halves.length - 1] : String(name ?? '');
};

/** The server's pre-fill for a Proof of Death (GET documents/funeral/:id/pod-defaults). */
interface PodDefaults {
  deceased_full_name: string | null;
  place_of_death: string | null;
  marital_status: string | null;
  marital_status_missing: boolean;
  marital_status_on_file: string | null;
  declarant_relationship: string | null;
  declarant_is_executor: boolean;
}

const fetchPodDefaults = async (funeralId: number, declarantId: number | ''): Promise<PodDefaults | null> => {
  try {
    const token = await getToken();
    const query = declarantId ? `?declarant_client_id=${declarantId}` : '';
    const response = await fetch(`${PAPERMAN_API_URL}documents/funeral/${funeralId}/pod-defaults${query}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.ok ? ((await response.json()) as PodDefaults) : null;
  } catch {
    return null;
  }
};

interface IssueDocumentDialogProps {
  open: boolean;
  onClose: () => void;
  funeral: any;
  onSuccess: () => void;
  defaultDocType?: 'POD' | 'POC';
}

export function IssueDocumentDialog({ open, onClose, funeral, onSuccess, defaultDocType }: IssueDocumentDialogProps) {
  const notify = useNotify();
  const { identity } = useGetIdentity();
  const [loading, setLoading] = useState(false);
  const [documentType, setDocumentType] = useState<'POD' | 'POC'>(defaultDocType || 'POD');
  const [issuerName, setIssuerName] = useState('');
  const [issuerTitle, setIssuerTitle] = useState('Funeral Director');
  const [deceasedName, setDeceasedName] = useState('');
  const [dateOfDeath, setDateOfDeath] = useState('');
  const [placeOfDeath, setPlaceOfDeath] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [cremationDate, setCremationDate] = useState('');
  const [cremationFacility, setCremationFacility] = useState('');
  const [declarantClientId, setDeclarantClientId] = useState<number | ''>('');
  const [maritalStatus, setMaritalStatus] = useState<string>('');
  const [declarantRelationship, setDeclarantRelationship] = useState('');
  const [podDefaults, setPodDefaults] = useState<PodDefaults | null>(null);
  // Set once the server draft has filled the case-level fields, so a later
  // re-read (declarant switch) or re-render doesn't clobber what staff typed.
  const draftAppliedRef = useRef(false);

  const { data: maritalStatuses } = useGetList(
    'marital_statuses',
    { pagination: { page: 1, perPage: 50 }, sort: { field: 'id', order: 'ASC' } },
    { enabled: open },
  );

  // Fetch deceased client data
  const { data: deceasedClient } = useGetOne(
    'clients',
    { id: funeral?.deceased_client_id },
    { enabled: !!funeral?.deceased_client_id && open },
  );

  // Fetch the crematorium partner to pre-fill the facility with its legal name.
  const { data: crematoriumPartner } = useGetOne(
    'partners',
    { id: funeral?.crematorium_partner_id },
    { enabled: !!funeral?.crematorium_partner_id && open },
  );

  // Build declarant options from BOTH sources: the funeral's role columns
  // (primary/buyer/authorized/declarant) and the funeral_clients join table.
  // Older funerals have no join-table rows — their clients live only in the
  // role columns — so reading the join table alone leaves the dropdown empty.
  const { data: funeralClients } = useGetManyReference(
    'funeral_clients',
    { target: 'funeral_id', id: funeral?.id ?? 0 },
    { enabled: !!funeral?.id && open },
  );

  const deceasedId = funeral?.deceased_client_id ? Number(funeral.deceased_client_id) : null;

  // Consolidate role labels per client id (a client can hold several roles),
  // excluding the deceased — they're never the declarant.
  const roleLabelByClientId = new Map<number, string[]>();
  const addRole = (id: any, label: string) => {
    const cid = id ? Number(id) : null;
    if (!cid || cid === deceasedId) return;
    const existing = roleLabelByClientId.get(cid);
    if (existing) existing.push(label);
    else roleLabelByClientId.set(cid, [label]);
  };
  addRole(funeral?.primary_client_id, 'Primary');
  addRole(funeral?.paying_client_id, 'Buyer');
  addRole(funeral?.authorized_client_id, 'Authorized');
  addRole(funeral?.declarant_client_id, 'Declarant');

  // Relationship strings from the join table (fallback label).
  const relationshipByClientId = new Map<number, string>(
    (funeralClients || []).map((fc: any) => [Number(fc.client_id), fc.relationship || '']),
  );

  // Merge ids from both sources, deduped, excluding the deceased.
  const declarantClientIds = Array.from(
    new Set<number>([
      ...roleLabelByClientId.keys(),
      ...(funeralClients || [])
        .map((fc: any) => Number(fc.client_id))
        .filter((id: number) => !!id && id !== deceasedId),
    ]),
  );
  const { data: declarantOptions } = useGetMany(
    'clients',
    { ids: declarantClientIds },
    { enabled: declarantClientIds.length > 0 && open },
  );

  // Sync defaultDocType prop
  useEffect(() => {
    if (open && defaultDocType) {
      setDocumentType(defaultDocType);
    }
  }, [open, defaultDocType]);

  // Pre-populate from funeral record + deceased client
  useEffect(() => {
    if (funeral && open) {
      setDateOfDeath(funeral.date_time_of_death
        ? new Date(funeral.date_time_of_death).toLocaleDateString('en-CA', { timeZone: 'America/New_York' })
        : '');
      // Fallback until (or if never) the server draft arrives.
      if (!draftAppliedRef.current) {
        setPlaceOfDeath(
          cityProvinceFromAddress(funeral.place_of_death_address) || funeral.place_of_death || '',
        );
      }
      setCremationDate(funeral.cremation_date ? String(funeral.cremation_date).split('T')[0] : '');
      const region = (funeral.region || '').toLowerCase();
      setIssuerName(region === 'ontario' ? 'Megan Zavitz' : 'Bram Paperman');
      setDeclarantClientId(
        funeral.declarant_client_id || funeral.authorized_client_id || '',
      );
    }
  }, [funeral, open, identity]);

  // The server's draft: place of death in the form the document wants
  // ("Hospital - Toronto, ON", "Residence - Oakville, ON"), the marital status,
  // and the declarant's relationship with "/ Executor" when the family named them
  // executor. Re-read when the declarant changes — the relationship is theirs.
  // The place of death is only taken on the first read so a typed correction
  // survives switching declarant.
  useEffect(() => {
    if (!open || !funeral?.id) return;
    let cancelled = false;
    void fetchPodDefaults(Number(funeral.id), declarantClientId).then((defaults) => {
      if (cancelled || !defaults) return;
      if (!draftAppliedRef.current) {
        draftAppliedRef.current = true;
        if (defaults.place_of_death) setPlaceOfDeath(defaults.place_of_death);
        // Includes the middle name, which the client-side fallback below drops.
        if (defaults.deceased_full_name) setDeceasedName(defaults.deceased_full_name);
        // Ontario leaves it off by default unless there is a living spouse;
        // '' (nothing on file) makes staff choose.
        setMaritalStatus(defaults.marital_status || (defaults.marital_status_missing ? '' : LEAVE_OFF));
      }
      setPodDefaults(defaults);
      setDeclarantRelationship(defaults.declarant_relationship || '');
    });
    return () => {
      cancelled = true;
    };
  }, [open, funeral?.id, declarantClientId]);

  useEffect(() => {
    if (!open) {
      setPodDefaults(null);
      draftAppliedRef.current = false;
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      const client = deceasedClient || funeral?.deceased_client;
      if (client) {
        // Fallback until the server draft arrives; never overwrite it.
        if (!draftAppliedRef.current) {
          setDeceasedName(
            [client.first_name, client.middle_name, client.last_name].filter(Boolean).join(' ').trim()
              || client.name || '',
          );
        }
        const dob = client.birthdate || client.date_of_birth;
        setDateOfBirth(dob ? String(dob).split('T')[0] : '');
      }
    }
  }, [deceasedClient, funeral, open]);

  // Pre-fill the cremation facility with the crematorium's official legal name
  // (falls back to its display name).
  useEffect(() => {
    if (open && crematoriumPartner) {
      setCremationFacility(crematoriumPartner.legal_name || crematoriumPartner.name || '');
    }
  }, [open, crematoriumPartner]);

  const handleSubmit = async () => {
    if (!deceasedName || !dateOfDeath) {
      notify('Deceased name and date of death are required', { type: 'error' });
      return;
    }
    if (documentType === 'POD' && podDefaults && maritalStatus === '') {
      notify('Choose a marital status, or "Leave it off the document"', { type: 'error' });
      return;
    }
    if (documentType === 'POC' && !cremationDate) {
      notify('Cremation date is required for Proof of Cremation', { type: 'error' });
      return;
    }

    setLoading(true);
    try {
      const token = await getToken();
      const response = await fetch(`${PAPERMAN_API_URL}documents/issue`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          document_type: documentType,
          case_id: Number(funeral.id),
          issuer_name: issuerName,
          issuer_title: issuerTitle,
          deceased_full_name: deceasedName,
          date_of_death: dateOfDeath,
          place_of_death: placeOfDeath,
          date_of_birth: dateOfBirth,
          cremation_date: cremationDate || undefined,
          cremation_facility: cremationFacility || undefined,
          declarant_client_id: declarantClientId || undefined,
          // Only once the server draft loaded — otherwise let the server derive
          // them, as before.
          ...(documentType === 'POD' && podDefaults
            ? {
                marital_status: maritalStatus === LEAVE_OFF ? null : maritalStatus,
                declarant_relationship: declarantRelationship.trim() || null,
              }
            : {}),
        }),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.message || 'Failed to issue document');
      }

      const result = await response.json();
      notify(`Document issued: ${result.verification_id}`, { type: 'success' });
      onSuccess();
      onClose();
    } catch (e: any) {
      notify(e.message || 'Failed to issue document', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Issue Verified Document</DialogTitle>
      <DialogContent>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 8 }}>
          <TextField
            select
            label="Document Type"
            value={documentType}
            onChange={e => setDocumentType(e.target.value as 'POD' | 'POC')}
            fullWidth
          >
            <MenuItem value="POD">Proof of Death</MenuItem>
            <MenuItem value="POC">Proof of Cremation</MenuItem>
          </TextField>

          <TextField
            label="Deceased Full Name"
            value={deceasedName}
            onChange={e => setDeceasedName(e.target.value)}
            fullWidth
            required
          />

          <TextField
            label="Date of Death"
            type="date"
            value={dateOfDeath}
            onChange={e => setDateOfDeath(e.target.value)}
            fullWidth
            required
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            label="Date of Birth"
            type="date"
            value={dateOfBirth}
            onChange={e => setDateOfBirth(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            label="Place of Death"
            value={placeOfDeath}
            onChange={e => setPlaceOfDeath(e.target.value)}
            fullWidth
            helperText={'e.g. "Toronto General Hospital - Toronto, ON" or "Residence - Oakville, ON"'}
          />

          {documentType === 'POD' && podDefaults && (
            <TextField
              select
              label="Marital Status"
              value={maritalStatus}
              onChange={e => setMaritalStatus(e.target.value)}
              fullWidth
              error={maritalStatus === ''}
              helperText={
                maritalStatus === ''
                  ? 'No marital status on file for the deceased — choose one, or leave it off the document.'
                  : maritalStatus === LEAVE_OFF && !podDefaults.marital_status && podDefaults.marital_status_on_file
                    ? `On file: ${englishHalf(podDefaults.marital_status_on_file)}. Left off by default — shown only with a living spouse or partner.`
                    : 'Only changes this document — the deceased\'s record is not edited.'
              }
            >
              <MenuItem value={LEAVE_OFF}>Leave it off the document</MenuItem>
              {(maritalStatuses || []).map((m: any) => (
                <MenuItem key={m.id} value={m.name}>{englishHalf(m.name)}</MenuItem>
              ))}
            </TextField>
          )}

          <TextField
            select
            label="Declarant"
            value={declarantClientId}
            onChange={e => setDeclarantClientId(e.target.value === '' ? '' : Number(e.target.value))}
            fullWidth
            helperText="Client shown under the Declarant section on the document"
          >
            <MenuItem value="">— None —</MenuItem>
            {(declarantOptions || []).map((c: any) => {
              const name = `${c.first_name || ''} ${c.last_name || ''}`.trim() || c.name || `Client ${c.id}`;
              const roles = roleLabelByClientId.get(Number(c.id));
              const label = roles && roles.length > 0
                ? roles.join(' / ')
                : relationshipByClientId.get(Number(c.id));
              return (
                <MenuItem key={c.id} value={c.id}>
                  {name}{label ? ` — ${label}` : ''}
                </MenuItem>
              );
            })}
          </TextField>

          {documentType === 'POD' && podDefaults && !!declarantClientId && (
            <TextField
              label="Declarant Relationship"
              value={declarantRelationship}
              onChange={e => setDeclarantRelationship(e.target.value)}
              fullWidth
              helperText={
                podDefaults.declarant_is_executor
                  ? '"/ Executor" added — the family said this person is the executor named in the will.'
                  : 'e.g. "Wife / Executor" when the declarant is also the executor.'
              }
            />
          )}

          {documentType === 'POC' && (
            <>
              <TextField
                label="Cremation Date"
                type="date"
                value={cremationDate}
                onChange={e => setCremationDate(e.target.value)}
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Cremation Facility"
                value={cremationFacility}
                onChange={e => setCremationFacility(e.target.value)}
                fullWidth
              />
            </>
          )}

          <TextField
            label="Issuer Name"
            value={issuerName}
            onChange={e => setIssuerName(e.target.value)}
            fullWidth
            required
          />

          <TextField
            label="Issuer Title"
            value={issuerTitle}
            onChange={e => setIssuerTitle(e.target.value)}
            fullWidth
            required
          />

          <Alert severity="info" sx={{ fontSize: '12px' }}>
            Review all fields carefully. Once issued, the document cannot be edited — only reissued with corrections.
          </Alert>
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? <CircularProgress size={20} /> : 'Generate Document'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
